// Standard user-facing errors (specs/00-standards.md §6). Tool logic throws UserError for every
// expected failure; the UI shows `message` in an ErrorBanner and never a raw exception.

export type ErrorCategory =
  | 'empty'
  | 'invalid'
  | 'range'
  | 'unsupported'
  | 'too-large'
  | 'timeout'
  | 'cancelled'
  | 'load-failed'
  | 'browser-support'
  | 'file'
  | 'network'
  | 'unexpected';

export interface UserErrorInit {
  category: ErrorCategory;
  /** Short stable code, safe to send to analytics, e.g. "invalid_json". */
  code: string;
  /** Plain message in the standard format: "<What is wrong>: <detail>". */
  message: string;
  line?: number;
  column?: number;
  /** One-line fix hint shown after the message. */
  hint?: string;
}

export class UserError extends Error {
  readonly category: ErrorCategory;
  readonly code: string;
  readonly line?: number;
  readonly column?: number;
  readonly hint?: string;

  constructor(init: UserErrorInit) {
    super(init.message);
    this.name = 'UserError';
    this.category = init.category;
    this.code = init.code;
    this.line = init.line;
    this.column = init.column;
    this.hint = init.hint;
  }
}

export function isUserError(err: unknown): err is UserError {
  return err instanceof UserError;
}

/** "Line 4, column 1" (or "" when the position is unknown). */
export function formatPosition(err: { line?: number; column?: number }): string {
  if (err.line == null) return '';
  return err.column != null ? `Line ${err.line}, column ${err.column}` : `Line ${err.line}`;
}

/** Message shown when something unexpected is thrown. Never includes the raw error text. */
export const UNEXPECTED_MESSAGE = 'Something went wrong in this tool. Your input is safe — try again, or report it.';

/**
 * Turns anything thrown into a UserError. UserErrors pass through; known library errors can be
 * mapped by passing `map`; everything else becomes a generic message (the detail goes to the console).
 * Each tool keeps its own `toUserError` wrapper that supplies `map` for its library.
 */
export function toUserError(
  err: unknown,
  map?: (err: unknown) => UserError | undefined,
): UserError {
  if (isUserError(err)) return err;
  const mapped = map?.(err);
  if (mapped) return mapped;
  if (typeof console !== 'undefined') console.error('[tool error]', err);
  return new UserError({ category: 'unexpected', code: 'unexpected', message: UNEXPECTED_MESSAGE });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

/**
 * Tool logic answers an empty input with a prompt such as "Enter text to encode". That is the empty-state hint,
 * not a failure, so the UI shows it as a neutral hint instead of an error banner (specs/00-standards.md §6.3).
 */
export function isEmptyInputPrompt(message: string): boolean {
  return /^(Enter|Paste|Please (enter|paste|fill in|add|provide|select))\b/i.test(message.trim());
}

/** "Input too large: 120 MB. The limit for this tool is 50 MB." (for tools whose logic returns {error} strings) */
export function sizeMessage(bytes: number, limitBytes: number, what = 'Input'): string {
  return `${what} too large: ${formatBytes(bytes)}. The limit for this tool is ${formatBytes(limitBytes)}.`;
}

/** Default text limit for tools that don't set their own (specs/00-standards.md §6.3). */
export const DEFAULT_TEXT_LIMIT = 10 * 1024 * 1024;

/** Size check to run BEFORE processing. Throws "Input too large: 120 MB. The limit for this tool is 50 MB." */
export function assertSize(bytes: number, limitBytes: number, what = 'Input'): void {
  if (bytes > limitBytes) {
    throw new UserError({
      category: 'too-large',
      code: 'too_large',
      message: sizeMessage(bytes, limitBytes, what),
    });
  }
}
